import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import {
  TerminologyMode,
  TerminologyConfig,
  TERMINOLOGY,
  DEFAULT_TERMINOLOGY_MODE,
  TERMINOLOGY_STORAGE_KEY,
} from '@/config/terminology';

interface TerminologyContextValue {
  /** Current terminology mode */
  mode: TerminologyMode;
  /** Set the terminology mode */
  setMode: (mode: TerminologyMode) => void;
  /** Toggle between FMEA and HAZID modes */
  toggleMode: () => void;
  /** Current terminology configuration (shorthand) */
  t: TerminologyConfig;
  /** Get a specific term by key */
  term: (key: keyof TerminologyConfig) => string;
  /** Check if currently in HAZID mode */
  isHazidMode: boolean;
}

const TerminologyContext = createContext<TerminologyContextValue | undefined>(undefined);

export function TerminologyProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<TerminologyMode>(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(TERMINOLOGY_STORAGE_KEY);
      if (saved === 'fmea' || saved === 'hazid') {
        return saved;
      }
    }
    return DEFAULT_TERMINOLOGY_MODE;
  });

  // Persist to localStorage when mode changes
  useEffect(() => {
    localStorage.setItem(TERMINOLOGY_STORAGE_KEY, mode);
  }, [mode]);

  const setMode = (newMode: TerminologyMode) => {
    setModeState(newMode);
  };

  const toggleMode = () => {
    setModeState((prev) => (prev === 'fmea' ? 'hazid' : 'fmea'));
  };

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo<TerminologyContextValue>(
    () => ({
      mode,
      setMode,
      toggleMode,
      t: TERMINOLOGY[mode],
      term: (key: keyof TerminologyConfig) => TERMINOLOGY[mode][key],
      isHazidMode: mode === 'hazid',
    }),
    [mode]
  );

  return <TerminologyContext.Provider value={value}>{children}</TerminologyContext.Provider>;
}

/**
 * Hook to access terminology context
 *
 * @example
 * const { t, mode, toggleMode } = useTerminology();
 * return <h1>{t.failureModes}</h1>; // "Failure Modes" or "Hazards"
 */
export const useTerminology = (): TerminologyContextValue => {
  const context = useContext(TerminologyContext);
  if (!context) {
    throw new Error('useTerminology must be used within TerminologyProvider');
  }
  return context;
};

/**
 * Convenience hook to get a single term
 *
 * @example
 * const label = useTerm('failureMode'); // "Failure Mode" or "Hazard"
 */
export const useTerm = (key: keyof TerminologyConfig): string => {
  const { t } = useTerminology();
  return t[key];
};
